import React from "react";
import "./HealthSummaryData.css"; // Assuming you have a CSS file for styles
import {
  CloudUploadOutlined,
  PlayCircleOutlined,
  PartitionOutlined,
  DesktopOutlined,
  WindowsOutlined,
  AndroidOutlined,
  WindowsFilled,
  AndroidFilled,
} from "@ant-design/icons";
import {
  Activity,
  Settings,
  Monitor,
  Cpu,
  HardDrive,
  MemoryStick,
} from "lucide-react";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../../global/ProLanguageContext";

const getBarColor = (percent) => {
  if (percent <= 60) return '#10b981'; // green — healthy
  if (percent <= 85) return '#f59e0b'; // amber — warning
  return '#ef4444';                    // red   — critical
};

const HealthSummaryData = ({ data }) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).analytics;

  if (!data) return <div className="health-summary-container">{t.noData}</div>;

  const ramPercent =
    data.ram_used && data.ram_total
      ? Math.round((data.ram_used / data.ram_total) * 100)
      : -1;
  const storagePercent =
    data.storage_used && data.storage_total
      ? Math.round((data.storage_used / data.storage_total) * 100)
      : -1;
  const cpuPercent =
    data.cpu_used && data.cpu_total
      ? Math.round((data.cpu_used / data.cpu_total) * 100)
      : -1;

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024; // The factor used to convert to the next unit (e.g., KB -> MB -> GB)
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]; // Units

    const i = Math.floor(Math.log(bytes) / Math.log(k)); // Determine the appropriate unit
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
    );
  }

  return (
    <div className="health-summary-container">
      <div className="summary-card">
        <div className="summary-title">
          <Monitor className="summary-icon" style={{ color: "#2563eb" }} />
          {t.deviceInfo}
        </div>
        <div className="summary-content-container">
          {"model" in data && (
            <div className="summary-content">
              <div className="sub-summary-title">{t.model}</div>
              <div className="sub-summary-content">{data.model}</div>
            </div>
          )}
          {"architecture" in data && (
            <div className="summary-content">
              <div className="sub-summary-title">{t.architecture}</div>
              <div className="sub-summary-content">{data.architecture}</div>
            </div>
          )}
          {"OS" in data && (
            <div className="summary-content">
              <div className="sub-summary-title">{t.platform}</div>
              <div className="sub-summary-content">
                {(data?.Platform ?? "").toLowerCase().includes("pc") ? (
                  <WindowsFilled
                    style={{ color: "#00A4EF", fontSize: "2rem" }}
                  />
                ) : (data?.Platform ?? "").toLowerCase().includes("android") ? (
                  <AndroidFilled
                    style={{ color: "#a4c639", fontSize: "2rem" }}
                  />
                ) : (
                  ""
                )}
              </div>
              {/* <div className="sub-summary-content">{data.OS.toLocaleLowerCase().includes("window") ? "PC" : "Android"}</div> */}
            </div>
          )}
          {"OSVersion" in data && (
            <div className="summary-content">
              <div className="sub-summary-title">{t.os}</div>
              <div className="sub-summary-content">
                {data.OS} ({data.OSVersion})
              </div>
            </div>
          )}
          {/* {data.map((item, index) => (
        <div key={index} className="summary-card">
          <div className="summary-value">{item.value}</div>
          <div className="summary-label">{item.label}</div>
        </div>
      ))} */}
        </div>
      </div>
      <div className="resource-summary-container">
        {ramPercent > -1 && (
          <div className="summary-half">
            <div className="summary-card">
              <div className="summary-title">
                <MemoryStick
                  className="summary-icon"
                  style={{ color: "#6b21a8" }}
                />
                {t.memoryUsage}
              </div>
              <div className="resource-content-container">
                <div className="status-header">
                  <div className="sub-summary-title">{t.ramUsage}</div>
                  <div className="sub-summary-content">{ramPercent} %</div>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${ramPercent}%`, backgroundColor: getBarColor(ramPercent) }}
                  ></div>
                </div>
              </div>

              <div className="resource-detail-container">
                <div className="resource-detail detail-gray">
                  <span className="resource-value">
                    {formatBytes(data.ram_total.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.total}</span>
                </div>
                <div className="resource-detail detail-red">
                  <span className="resource-value">
                    {formatBytes(data.ram_used.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.used}</span>
                </div>
                <div className="resource-detail detail-green">
                  <span className="resource-value">
                    {formatBytes(data.ram_free.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.free}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {storagePercent > -1 && (
          <div className="summary-half">
            <div className="summary-card">
              <div className="summary-title">
                <HardDrive
                  className="summary-icon"
                  style={{ color: "#2563eb" }}
                />
                {t.storageUsage}
              </div>

              <div className="resource-content-container">
                <div className="status-header">
                  <div className="sub-summary-title">{t.diskUsage}</div>
                  <div className="sub-summary-content">{storagePercent} %</div>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${storagePercent}%`, backgroundColor: getBarColor(storagePercent) }}
                  ></div>
                </div>
              </div>

              <div className="resource-detail-container">
                <div className="resource-detail detail-gray">
                  <span className="resource-value">
                    {formatBytes(data.storage_total.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.total}</span>
                </div>
                <div className="resource-detail detail-red">
                  <span className="resource-value">
                    {formatBytes(data.storage_used.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.used}</span>
                </div>
                <div className="resource-detail detail-green">
                  <span className="resource-value">
                    {formatBytes(data.storage_free.toFixed(2))}
                  </span>
                  <span className="resource-label">{t.free}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {cpuPercent > -1 && (
          <div className="summary-full">
            <div className="summary-card">
              <div className="summary-title">
                <Cpu className="summary-icon" style={{ color: "#ea580c" }} />
                {t.cpuUsage}
              </div>

              <div className="resource-content-container">
                <div className="status-header">
                  <div className="sub-summary-title">{t.processorUsage}</div>
                  <div className="sub-summary-content">{cpuPercent} %</div>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar"
                    style={{ width: `${cpuPercent}%`, backgroundColor: getBarColor(cpuPercent) }}
                  ></div>
                </div>
              </div>
              <div className="resource-detail-container">
                <div className="resource-detail detail-red">
                  <span className="resource-value">
                    {parseFloat(data.cpu_used).toFixed(2)} %
                  </span>
                  <span className="resource-label">{t.cpuUsed}</span>
                </div>
                <div className="resource-detail detail-green">
                  <span className="resource-value">
                    {(
                      parseFloat(data.cpu_total) - parseFloat(data.cpu_used)
                    ).toFixed(2)}{" "}
                    %
                  </span>
                  <span className="resource-label">{t.cpuAvailable}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthSummaryData;
